import React from 'react';

import { Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from 'react-query';

import ColoredButton from 'components/ColoredButton';
import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import type { ActionLibraryInstance } from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import checkAccess from 'config/permissions/checkAccess';
import RolesOrganization from 'config/permissions/RolesOrganization';
import action_library_messages from 'messages/action_library_messages';
import general_messages from 'messages/general_messages';
import userDataStorage from 'storages/userDataStorage';

type Props = {
  open: boolean;
  onClose: () => void;
  instance: ActionLibraryInstance;
  onSuccess: () => void;
};

const CompleteInstanceDialog: React.FC<Props> = ({ open, onClose, instance, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const [comment, setComment] = React.useState('');
  const [commentError, setCommentError] = React.useState<string | null>(null);

  const userData = userDataStorage.get() ?? {};
  const currentUserId = (userData as { id?: number }).id;
  const isOwner = instance.owner != null && currentUserId != null && instance.owner === currentUserId;
  const isAssetManager = checkAccess(userData.roles ?? [], [RolesOrganization.AssetsManager]);
  const requiresComment = isOwner && !isAssetManager;

  const completeMutation = useMutation(
    (completionComment: string) =>
      actionLibraryInstancesApi.completeActionLibraryInstance(instance.id, {
        completionComment: completionComment || undefined,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('ACTION_LIBRARY_INSTANCES_GET_ALL');
        enqueueSnackbar(t(action_library_messages.success.instance_updated), { variant: 'success' });
        onSuccess();
        setComment('');
        setCommentError(null);
        onClose();
      },
      onError: (err: unknown) => {
        const axiosErr = err as { response?: { status?: number; data?: unknown } };
        const status = axiosErr?.response?.status;
        const data = axiosErr?.response?.data;

        if (status === 403) {
          enqueueSnackbar(t(action_library_messages.errors.complete_forbidden), { variant: 'error' });
          return;
        }

        if (status === 400 && data && typeof data === 'object') {
          const d = data as { completion_comment?: string[]; detail?: string | string[] };
          const fieldErr = d.completion_comment?.[0];
          if (fieldErr) {
            setCommentError(fieldErr);
            return;
          }
          let detailStr = '';
          if (typeof d.detail === 'string') {
            detailStr = d.detail;
          } else if (Array.isArray(d.detail)) {
            detailStr = String(d.detail[0] ?? '');
          }
          if (detailStr.trim()) {
            setCommentError(detailStr);
            return;
          }
        }

        enqueueSnackbar(t(action_library_messages.errors.complete_failed), { variant: 'error' });
      },
    },
  );

  const handleClose = () => {
    setComment('');
    setCommentError(null);
    onClose();
  };

  const handleSubmit = () => {
    const trimmed = comment.trim();
    if (requiresComment && trimmed.length === 0) {
      setCommentError(t(action_library_messages.instance_actions.complete_dialog_comment_required_owner));
      return;
    }
    setCommentError(null);
    completeMutation.mutate(trimmed);
  };

  const commentTrimmed = comment.trim();
  const canSubmit = !completeMutation.isLoading && (!requiresComment || commentTrimmed.length > 0);

  return (
    <Dialog onClose={handleClose} open={open}>
      <DialogTitle>{t(action_library_messages.instance_actions.complete_dialog_title)}</DialogTitle>
      <DialogContent>
        <TextField
          error={Boolean(commentError)}
          fullWidth
          helperText={
            commentError ??
            (requiresComment ? t(action_library_messages.instance_actions.complete_dialog_comment_required_owner) : undefined)
          }
          label={t(action_library_messages.instance_actions.complete_dialog_comment_placeholder)}
          multiline
          onChange={e => {
            setComment(e.target.value);
            if (commentError) setCommentError(null);
          }}
          required={requiresComment}
          rows={3}
          value={comment}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={false}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel[0], general_messages.cancel[1])}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={!canSubmit}
          icon={false}
          onClick={handleSubmit}
          showLoader={completeMutation.isLoading}
          textLike={false}
          variant='contained'
        >
          {t(action_library_messages.instance_actions.complete_instance)}
        </ColoredButton>
      </DialogActions>
    </Dialog>
  );
};

export default CompleteInstanceDialog;
